import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, BellOff, Plus, Smartphone, User, Users } from 'lucide-react';

import { HoverCardRoster } from '@/components/arc/hover-card/hover-card';
import { MultiSelect, type MultiSelectOption } from '@/components/arc/multi-select/multi-select';

import type { TenantPushUser } from '../PushAPI';
import { PushUsersState } from './usePushUsers';
import { PUSH_ROLE_IDS, pushUserName, roleLabelKey, userHasRole } from './pushRecipients';
import type { OwnRole } from './useOwnRoles';

type Translate = (key: string, values?: Record<string, string>) => string;

const T = 'settingsPanel.settingsPanel.alertManagementPanel.push.';

/** Where roles are created: the team management (a new tab, so the alert being edited stays open). */
const ROLE_CREATION_PATH = '/settings?panel=teamManagement';

const ROLE_PREFIX = 'role:';
const USER_PREFIX = 'user:';

/** A hover loads the people afresh, but not more often than this (a pointer sweeping over the roles is no reason for three requests). */
const HOVER_REFRESH_MIN_MS = 4000;

/** How many members a role's tooltip names before it says "and n more". */
const MAX_MEMBERS_SHOWN = 40;

/** People who can receive push first, then by name: the ones an alert actually reaches are on top. */
const byPushThenName = (a: TenantPushUser, b: TenantPushUser) =>
    Number(b.pushActive) - Number(a.pushActive) || pushUserName(a).localeCompare(pushUserName(b), 'de');

/** What hovering a role shows: who sits behind it and who of them can receive push. */
function RoleMembers({ t, roleName, members }: { t: Translate; roleName: string; members: TenantPushUser[] }) {
    const active = members.filter((member) => member.pushActive).length;

    return (
        <HoverCardRoster
            title={roleName}
            subtitle={t(`${T}reachCount`, { active: String(active), total: String(members.length) })}
            icon={<Users size={18} strokeWidth={1.75} />}
            rows={[...members].sort(byPushThenName).map((member) => ({
                key: member.userId,
                name: pushUserName(member),
                status: t(`${T}${member.pushActive ? 'stateActive' : 'stateNone'}`),
                positive: member.pushActive,
            }))}
            emptyText={t(`${T}roleEmpty`)}
            maxRows={MAX_MEMBERS_SHOWN}
            moreText={(hidden) => t(`${T}moreMembers`, { n: String(hidden) })}
        />
    );
}

/**
 * Recipients of a push action: the customer roles (all members) and individual users of the tenant,
 * chosen in any mix in one multi-select (uiarc `multi-select`, extended for the portal: search, sections,
 * a detail on the right of every row, chips with a remove button).
 *
 * Every person says on the right whether they can receive push notifications; a role says how many of
 * its members can. The users come from `GET /customers/:customer/push/users`; while that is not
 * available, roles can still be chosen and chosen people stay listed.
 */
export default function AlertPushRecipientPicker(properties: {
    t: Translate;
    idPrefix: string;
    state: PushUsersState;
    recipientRoles: string[];
    recipientUsers: string[];
    /** The customer's own roles, listed after the three system roles. */
    ownRoles?: OwnRole[];
    /** Loads people and roles again; without it the list has no "Aktualisieren". */
    onRefresh?: () => void;
    onChange: (recipientRoles: string[], recipientUsers: string[]) => void;
}) {
    const { t, state, recipientRoles, recipientUsers } = properties;
    // A reload keeps the people it had: the list stays usable while it loads.
    const refreshing = state.status === 'loading' && state.users.length > 0;
    const ready = state.status === 'ready' || refreshing;
    const ownRoles = properties.ownRoles;
    // Opening a role's card loads people and roles again, so the card shows who is in the group now.
    // "Aktualisiere …" shows only for a refresh the user asked for; the silent one behind a hover shows nothing.
    const [manualRefresh, setManualRefresh] = useState(false);

    const wasRefreshing = useRef(false);

    useEffect(() => {
        // Cleared when a load ends, not on the render right after the click (the load has not started yet then).
        if (wasRefreshing.current && !refreshing) setManualRefresh(false);
        wasRefreshing.current = refreshing;
    }, [refreshing]);
    const lastHoverRefresh = useRef(0);
    const onRefresh = properties.onRefresh;
    const refreshOnHover = onRefresh
        ? () => {
            const now = Date.now();

            if (now - lastHoverRefresh.current >= HOVER_REFRESH_MIN_MS) {
                lastHoverRefresh.current = now;
                onRefresh();
            }
        }
        : undefined;

    const options = useMemo(() => {
        const roleChoices = [
            ...PUSH_ROLE_IDS.map((roleId) => ({ id: roleId as string, name: t(roleLabelKey(roleId)) })),
            ...(ownRoles ?? []).filter((role) => !(PUSH_ROLE_IDS as readonly string[]).includes(role.id)),
        ];
        const roleOptions: MultiSelectOption[] = roleChoices.map(({ id: roleId, name }) => {
            const members = ready ? state.users.filter((user) => userHasRole(user, roleId)) : null;
            const detail = members
                ? t(`${T}reachCount`, {
                    active: String(members.filter((member) => member.pushActive).length),
                    total: String(members.length),
                })
                : undefined;

            return {
                value: `${ROLE_PREFIX}${roleId}`,
                label: name,
                group: t(`${T}sectionRoles`),
                icon: <Users size={16} strokeWidth={1.75} />,
                detail,
                chipTitle: detail,
                hover: members ? <RoleMembers t={t} roleName={name} members={members} /> : undefined,
                onHoverOpen: refreshOnHover,
            };
        });
        // Roles are made in the team management: the row opens it in a new tab and is never a choice itself.
        roleOptions.push({
            value: 'action:new-role',
            label: t(`${T}newRole`),
            group: t(`${T}sectionRoles`),
            icon: <Plus size={16} strokeWidth={1.75} />,
            actionIcon: <ArrowUpRight size={14} strokeWidth={1.75} />,
            action: () => window.open(ROLE_CREATION_PATH, '_blank', 'noopener,noreferrer'),
        });
        const people: MultiSelectOption[] = ready
            ? [...state.users].sort(byPushThenName).map((user) => {
                const status = t(`${T}${user.pushActive ? 'canReceivePush' : 'cannotReceivePush'}`);

                return {
                    value: `${USER_PREFIX}${user.userId}`,
                    label: pushUserName(user),
                    description: user.email,
                    group: t(`${T}sectionPeople`),
                    icon: user.pushActive
                        ? <Smartphone size={16} strokeWidth={1.75} className="text-success" />
                        : <BellOff size={16} strokeWidth={1.75} className="text-amber-600" />,
                    detail: status,
                    detailTone: user.pushActive ? ('positive' as const) : undefined,
                    chipTitle: status,
                };
            })
            : [];
        // A chosen person the list does not know (still loading, or deleted since) stays selected and named.
        const known = new Set(people.map((option) => option.value));
        const unknown: MultiSelectOption[] = recipientUsers
            .filter((userId) => !known.has(`${USER_PREFIX}${userId}`))
            .map((userId) => ({
                value: `${USER_PREFIX}${userId}`,
                label: t(`${T}unknownPerson`),
                icon: <User size={16} strokeWidth={1.75} />,
                hidden: true,
            }));

        const knownRoles = new Set(roleOptions.map((option) => option.value));
        const unknownRoles: MultiSelectOption[] = recipientRoles
            .filter((roleId) => !knownRoles.has(`${ROLE_PREFIX}${roleId}`))
            .map((roleId) => ({ value: `${ROLE_PREFIX}${roleId}`, label: roleId, icon: <Users size={16} strokeWidth={1.75} />, hidden: true }));

        return [...roleOptions, ...unknownRoles, ...people, ...unknown];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, refreshing, state.users, recipientUsers, recipientRoles, ownRoles, t]);

    // "Aktualisieren" next to both group headings, behind a grey rule: it loads people and roles again.
    const refresh = properties.onRefresh
        ? {
            label: t(manualRefresh && refreshing ? `${T}refreshing` : `${T}refresh`),
            onClick: () => {
                setManualRefresh(true);
                properties.onRefresh?.();
            },
            busy: manualRefresh && refreshing,
        }
        : undefined;
    const groupActions = refresh
        ? { [t(`${T}sectionRoles`)]: refresh, [t(`${T}sectionPeople`)]: refresh }
        : undefined;

    const value = [
        ...recipientRoles.map((roleId) => `${ROLE_PREFIX}${roleId}`),
        ...recipientUsers.map((userId) => `${USER_PREFIX}${userId}`),
    ];

    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <MultiSelect
                label={t(`${T}recipients`)}
                options={options}
                value={value}
                onValueChange={(next) =>
                    properties.onChange(
                        next.filter((entry) => entry.startsWith(ROLE_PREFIX)).map((entry) => entry.slice(ROLE_PREFIX.length)),
                        next.filter((entry) => entry.startsWith(USER_PREFIX)).map((entry) => entry.slice(USER_PREFIX.length)),
                    )
                }
                placeholder={t(`${T}recipientsPlaceholder`)}
                searchable
                searchPlaceholder={t(`${T}recipientsSearch`)}
                emptyText={t(`${T}peopleNone`)}
                clearLabel={t(`${T}recipientsClear`)}
                removeLabel={(name) => t(`${T}removePerson`, { name })}
                wrap
                maxMenuHeight="18rem"
                groupActions={groupActions}
            />
            {!ready && (
                <p className="text-xs text-muted-foreground">
                    {state.status === 'error' ? t(`${T}peopleUnavailable`) : t(`${T}peopleLoading`)}
                </p>
            )}
        </div>
    );
}
